import { useDroppable } from "@dnd-kit/core";
import * as React from "react";

import { Badge, Button, Skeleton } from "@/components/ui";
import { cn } from "@/lib/utils";
import { VideoProject } from "@/services/bedrock";

import { VideoProjectKanbanCardSkeleton, VideoProjectKanbanColumnSkeletons } from "./KanbanCardSkeleton";
import {
  VideoProjectKanbanCardSkeletonLayout,
  VideoProjectKanbanColumnDef,
  VideoProjectKanbanColumnDropState,
} from "./types";

export const KANBAN_DROP_PLACEHOLDER_ATTR = "data-kanban-drop-placeholder";

interface VideoProjectKanbanColumnProps<Id extends string> {
  column: Pick<VideoProjectKanbanColumnDef<Id>, "id" | "label">;
  projects: VideoProject[];
  totalCount: number;
  loading?: boolean;
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: (columnId: Id) => void;
  renderCard: (project: VideoProject) => React.ReactNode;
  cardSkeleton?: VideoProjectKanbanCardSkeletonLayout;
  headerAccessory?: React.ReactNode;
  headerAccessoryLoading?: React.ReactNode;
  showCount?: boolean;
  droppable?: boolean;
  dropState?: VideoProjectKanbanColumnDropState;
  hideEmpty?: boolean;
  dropPlaceholderIndex?: number | null;
  dropPlaceholderHeight?: number;
}

function KanbanDropPlaceholder({ height }: { height?: number }) {
  return (
    <div
      aria-hidden="true"
      {...{ [KANBAN_DROP_PLACEHOLDER_ATTR]: "" }}
      className="rounded-lg bg-emerald-50/70 ring-2 ring-dashed ring-emerald-400/80"
      style={height != null ? { height } : { minHeight: 72 }}
    />
  );
}

function VideoProjectKanbanColumnChrome<Id extends string>({
  column,
  projects,
  totalCount,
  loading = false,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
  renderCard,
  cardSkeleton = "player",
  headerAccessory,
  headerAccessoryLoading,
  showCount = true,
  dropState = "idle",
  hideEmpty = false,
  dropPlaceholderIndex = null,
  dropPlaceholderHeight,
  contentRef,
}: VideoProjectKanbanColumnProps<Id> & { contentRef?: React.Ref<HTMLDivElement> }) {
  const isInvalid = dropState === "invalid";
  const cards = projects.map(project => <React.Fragment key={project.id}>{renderCard(project)}</React.Fragment>);

  if (dropPlaceholderIndex != null) {
    cards.splice(
      Math.min(dropPlaceholderIndex, cards.length),
      0,
      <KanbanDropPlaceholder key="kanban-drop-placeholder" height={dropPlaceholderHeight} />
    );
  }

  return (
    <div className={cn("flex flex-col min-w-60 flex-1 transition-opacity duration-200", isInvalid && "opacity-45")}>
      <div
        className={cn("flex items-center justify-between mb-3 px-0.5 transition-colors", isInvalid && "text-slate-400")}
      >
        <h3 className="text-xs font-semibold leading-tight">{column.label}</h3>
        <div className="flex items-center gap-1.5">
          {loading ? (
            <>
              {headerAccessoryLoading}
              {showCount ? <Skeleton className="h-[22px] w-7 rounded-full" /> : null}
            </>
          ) : (
            <>
              {headerAccessory}
              {showCount ? (
                <Badge
                  variant="outline"
                  size="small"
                  className={cn("bg-white border-slate-200", isInvalid ? "text-slate-400" : "text-slate-600")}
                  title={`${totalCount} total`}
                  aria-label={`${totalCount} total ${totalCount === 1 ? "project" : "projects"}`}
                >
                  {totalCount}
                </Badge>
              ) : null}
            </>
          )}
        </div>
      </div>
      <div
        ref={contentRef}
        aria-busy={loading || loadingMore}
        className={cn(
          "flex flex-col gap-2 min-h-[200px] flex-1 rounded-lg border border-dashed p-2 transition-[border-color,background-color,box-shadow] duration-200",
          dropState === "idle" && "border-slate-200 bg-slate-50/40",
          dropState === "source" && "border-slate-300 bg-slate-100/50",
          dropState === "invalid" && "border-slate-200 bg-slate-100/80 cursor-not-allowed",
          dropState === "valid" && "border-emerald-400 bg-emerald-50/40 ring-1 ring-emerald-200/60",
          dropState === "valid-hover" && "border-emerald-500 bg-emerald-50/70 ring-2 ring-emerald-300/70"
        )}
      >
        {loading || loadingMore ? (
          <span className="sr-only" role="status">
            {loading ? `Loading ${column.label} projects` : `Loading more ${column.label} projects`}
          </span>
        ) : null}
        {loading ? <VideoProjectKanbanColumnSkeletons layout={cardSkeleton} /> : cards}
        {!loading && !hideEmpty && projects.length === 0 ? (
          <div className="text-[11px] text-slate-400 text-center py-4 px-2">No projects</div>
        ) : null}
        {!loading &&
          hasMore &&
          !hideEmpty &&
          (loadingMore ? (
            <VideoProjectKanbanCardSkeleton
              layout={cardSkeleton}
              className={cardSkeleton === "staff" ? "animate-in fade-in duration-200 opacity-70" : undefined}
            />
          ) : (
            <Button
              variant="ghost"
              size="xs"
              className="w-full text-[11px] text-slate-600 hover:text-slate-900"
              onClick={() => onLoadMore?.(column.id)}
            >
              Load More
            </Button>
          ))}
      </div>
    </div>
  );
}

function VideoProjectKanbanDroppableColumn<Id extends string>(props: VideoProjectKanbanColumnProps<Id>) {
  const { setNodeRef } = useDroppable({
    id: props.column.id,
    disabled: props.dropState === "invalid",
  });

  return <VideoProjectKanbanColumnChrome {...props} contentRef={setNodeRef} />;
}

export function VideoProjectKanbanColumn<Id extends string>(props: VideoProjectKanbanColumnProps<Id>) {
  if (props.droppable) {
    return <VideoProjectKanbanDroppableColumn {...props} />;
  }

  return <VideoProjectKanbanColumnChrome {...props} />;
}
